/**
 * Service for defect user role-based permissions.
 * Migrated from web-app-legacy/js/app/Components/Defects/Helpers/DefectUserRoleProvider.ts
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap, catchError, BehaviorSubject } from 'rxjs';
import { getConfig } from '../config.service';
import { IDefect, IEmployee, IUserInfo, DefectStateEnum } from './defect.models';
import { UserRole } from './defect-overview/defect-overview.models';

// Returned when the user info request fails. Never cached - see loadUserInfo().
const EMPTY_USER_INFO: IUserInfo = {
  employee: {
    personalNumber: '',
    functionalAreaId: 0,
    departmentId: 0,
    departmentName: '',
    titleBefore: '',
    name: '',
    surname: '',
    titleAfter: '',
    fullName: '',
    email: '',
    phone: '',
    isExternal: false,
    isBuildingManager: false,
  },
  roles: [],
};

@Injectable({
  providedIn: 'root',
})
export class DefectUserRoleService {
  private http = inject(HttpClient);
  private get baseUrl() { return getConfig().api.middlewareUrl; }

  // State constants matching legacy implementation
  private readonly SOLVED_STATE_ID = DefectStateEnum.Solved; // 3
  private readonly CRITICAL_SEVERITY_ID = 1;

  // User info cache
  private userInfo: IUserInfo | null = null;
  private userInfoLoaded$ = new BehaviorSubject<boolean>(false);

  /**
   * Load the current user's employee record and roles.
   * GET /api/service/UserInfo
   *
   * Not WebSettings/{culture}: that route resolves the user through the legacy
   * session store and requires an x-skoda-sessionid header, which this app has
   * no concept of. It answered 404 (no culture) or 400 (no session), so userInfo
   * stayed null and every permission check failed.
   */
  loadUserInfo(): Observable<IUserInfo> {
    if (this.userInfo) {
      return of(this.userInfo);
    }

    return this.http.get<IUserInfo>(`${this.baseUrl}/api/service/UserInfo`).pipe(
      tap((userInfo) => {
        this.userInfo = userInfo;
        this.userInfoLoaded$.next(true);
      }),
      catchError((error) => {
        console.error('[DefectUserRoleService] Error loading user info:', error);
        // Deliberately not cached: caching the empty fallback would make every
        // later role check fail for the rest of the session, even once the API
        // recovers. Leaving userInfo null lets the next call retry.
        return of(EMPTY_USER_INFO);
      })
    );
  }

  /**
   * Get cached user info (returns null if not loaded).
   */
  getUserInfo(): IUserInfo | null {
    return this.userInfo;
  }

  /**
   * Get current user's personal number.
   */
  getUserPersonalNumber(): string {
    return this.userInfo?.employee?.personalNumber ?? '';
  }

  /**
   * Check if current user is in a specific role.
   */
  isInRole(role: UserRole): boolean {
    if (!this.userInfo?.roles) {
      return false;
    }

    return this.userInfo.roles.includes(role);
  }

  /**
   * Check if current user can edit a defect.
   * Logic migrated from DefectUserRoleProvider.canEditDefect
   */
  canEditDefect(defect: IDefect): boolean {
    if (!this.userInfo?.roles || !this.userInfo?.employee) {
      return false;
    }

    const userPersonalNumber = this.userInfo.employee.personalNumber;

    for (const role of this.userInfo.roles) {
      let canEdit = false;

      switch (role) {
        case UserRole.User:
          // Can edit if: created it AND state < Solved
          canEdit =
            this.isCreator(defect.createdByPersonalNumber, userPersonalNumber) &&
            defect.defectStateId < this.SOLVED_STATE_ID;
          break;

        case UserRole.Manager:
          // Can edit if: is manager of this defect, OR (created it AND state <= Solved)
          canEdit =
            this.isDefectManager(defect.managers, userPersonalNumber) ||
            (this.isCreator(defect.createdByPersonalNumber, userPersonalNumber) &&
              defect.defectStateId <= this.SOLVED_STATE_ID);
          break;

        case UserRole.Coordinator:
          // Can edit if: is coordinator of this defect, OR (created it AND state <= Solved)
          canEdit =
            this.isDefectCoordinator(defect.coordinator, userPersonalNumber) ||
            (this.isCreator(defect.createdByPersonalNumber, userPersonalNumber) &&
              defect.defectStateId <= this.SOLVED_STATE_ID);
          break;

        case UserRole.Dispatcher:
          // Can edit only if: severity = Critical AND state < Solved
          canEdit =
            defect.severityLevelId === this.CRITICAL_SEVERITY_ID &&
            defect.defectStateId < this.SOLVED_STATE_ID;
          break;

        case UserRole.Administrator:
          // Administrators can always edit
          canEdit = true;
          break;
      }

      if (canEdit) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if export is allowed for current user.
   * Export is allowed for Manager or Administrator roles.
   */
  canExport(): boolean {
    return this.isInRole(UserRole.Manager) || this.isInRole(UserRole.Administrator);
  }

  /**
   * Check if SAP integration is enabled for current user.
   * SAP integration requires Manager, Administrator, or Coordinator role.
   */
  isSapIntegrationEnabled(): boolean {
    return (
      this.isInRole(UserRole.Manager) ||
      this.isInRole(UserRole.Administrator) ||
      this.isInRole(UserRole.Coordinator)
    );
  }

  private isCreator(creatorNumber: string, employeeNumber: string): boolean {
    return creatorNumber === employeeNumber;
  }

  private isDefectManager(managers: IEmployee[], userPersonalNumber: string): boolean {
    if (!managers) {
      return false;
    }
    return managers.some((m) => m.personalNumber === userPersonalNumber);
  }

  private isDefectCoordinator(coordinator: IEmployee | null, userPersonalNumber: string): boolean {
    if (!coordinator) {
      return false;
    }
    return coordinator.personalNumber === userPersonalNumber;
  }
}
